import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RideWiseRow, TaxiCallerRow, VehicleRow, DriverRow } from "./types";
import { fmtNum, safeArr, toMonthKey } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  tc: TaxiCallerRow[];
  vehiclesById: Map<string, VehicleRow>;
  driversById: Map<string, DriverRow>;
  loading: boolean;
}

const DataGapsTab: React.FC<Props> = ({ rw, tc, vehiclesById, driversById, loading }) => {
  const summary = useMemo(() => {
    const rwList = safeArr(rw);
    const tcList = safeArr(tc);

    const rwMissingDriver = rwList.filter((r) => !driversById.get(String(r.driver_number || ""))).length;
    const rwMissingVehicle = rwList.filter((r) => !vehiclesById.get(String(r.vehicle_identifier || ""))).length;
    const rwMissingClient = rwList.filter((r) => !(r.client_number || "").trim()).length;
    const rwMissingFare = rwList.filter((r) => r.fare_total == null).length;
    const rwMissingKm = rwList.filter((r) => r.kilometres == null).length;

    const tcMissingDate = tcList.filter((r) => !r.pu_date).length;
    const tcMissingCC = tcList.filter((r) => !(r.Cost_Code || "").trim()).length;

    const rwMonths = new Set(rwList.map((r) => toMonthKey(r.trip_date)).filter(Boolean));
    const tcMonths = new Set(tcList.map((r) => toMonthKey(r.pu_date)).filter(Boolean));
    const tcMissingMonths = Array.from(rwMonths)
      .filter((m) => !tcMonths.has(m) && m >= "2025-07")
      .sort();

    return {
      rwTotal: rwList.length,
      tcTotal: tcList.length,
      rwMissingDriver, rwMissingVehicle, rwMissingClient, rwMissingFare, rwMissingKm,
      tcMissingDate, tcMissingCC,
      tcMissingMonths,
    };
  }, [rw, tc, vehiclesById, driversById]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading data gap analysis…</CardContent>
      </Card>
    );
  }

  const Stat = ({ label, value, total, danger = false }: { label: string; value: number; total: number; danger?: boolean }) => {
    const pct = total ? (value / total) * 100 : 0;
    return (
      <Card className="bg-card border border-border">
        <CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`text-xl font-bold ${danger && value > 0 ? "text-red-500" : ""}`}>{fmtNum(value)}</div>
          <div className="text-xs text-muted-foreground">{pct.toFixed(1)}% of {fmtNum(total)}</div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="bg-card border border-border">
        <CardHeader><CardTitle className="text-base">RideWise Data Gaps</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat label="Missing Driver Match" value={summary.rwMissingDriver} total={summary.rwTotal} danger />
            <Stat label="Missing Vehicle Match" value={summary.rwMissingVehicle} total={summary.rwTotal} danger />
            <Stat label="Missing Client #" value={summary.rwMissingClient} total={summary.rwTotal} danger />
            <Stat label="Missing Fare Total" value={summary.rwMissingFare} total={summary.rwTotal} />
            <Stat label="Missing Kilometres" value={summary.rwMissingKm} total={summary.rwTotal} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border border-border">
        <CardHeader><CardTitle className="text-base">TaxiCaller Data Gaps</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Missing PU Date" value={summary.tcMissingDate} total={summary.tcTotal} danger />
            <Stat label="Missing Cost Code" value={summary.tcMissingCC} total={summary.tcTotal} />
            <Card className="bg-card border border-border">
              <CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Months in RideWise but not TaxiCaller</div>
                <div className="text-xl font-bold">{fmtNum(summary.tcMissingMonths.length)}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {summary.tcMissingMonths.slice(0, 12).map((m) => (
                    <Badge key={m} variant="outline">{m}</Badge>
                  ))}
                  {summary.tcMissingMonths.length > 12 && (
                    <Badge variant="outline">+{summary.tcMissingMonths.length - 12} more</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Note: TaxiCaller data only exists from 2025-07 onwards.
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataGapsTab;
